import { useContext } from 'react';
import { InputAdornment, Box, Typography } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import type { Discount } from '../../../types/models/Discount';
import { AppContext, SttDialog } from '../../../sporttia/all';
import { getErrorMessage } from '../../../lib/utils';
import { useLoader } from '../../../lib/hooks';
import SttValidatedForm from '../../../sporttia/SttValidatedForm';
import useDiscountService from '../../../services/DiscountsService';
import useScsService from '../../../services/ScsService';
import { PERMISSIONS } from '../../../lib/permissions';

type DiscountEditDialogProps = {
    discount: Discount | undefined;
    onUpdate?: (discount: Discount) => void;
    onSave?: (discount: Discount) => void;
    onDelete?: (discount: Discount) => void;
    onClose?: () => void;
};

export default function DiscountEditDialog({
    discount,
    onUpdate,
    onSave,
    onDelete,
    onClose,
}: DiscountEditDialogProps) {
    const cxt = useContext(AppContext)!;
    const { t } = useTranslation();
    const form = useForm({
        defaultValues: {
            name: discount?.name,
            discountPercentage: discount?.discountPercentage,
        },
    });

    const discountService = useDiscountService();
    const scsService = useScsService();

    const createScsDiscountMutation = scsService.useCreateDiscount({
        onSuccess: (data: any) => {
            onSave?.(data.discount);
            cxt.showMessage('S', t('discountCreated'));
        },
        onError: (error: any) => {
            cxt.showMessage('E', getErrorMessage(error));
        },
    });

    const updateDiscountMutation = discountService.useUpdateDiscount({
        onSuccess: (data: any) => {
            onUpdate?.(data.discount);
            cxt.showMessage('S', t('discountUpdated'));
        },
        onError: (error: any) => {
            cxt.showMessage('E', getErrorMessage(error));
        },
    });

    const deleteDiscountMutation = discountService.useDeleteDiscount({
        onSuccess: (data: any) => {
            onDelete?.(data.discount);
            cxt.showMessage('S', t('discountDeleted'));
        },
        onError: (error: any) => {
            cxt.showMessage('E', getErrorMessage(error));
        },
    });

    const handleOnSubmit = () => {
        form.handleSubmit(({ name, discountPercentage }: any) => {
            if (name === undefined || discountPercentage === undefined) {
                return;
            }

            const params = {
                name,
                discountPercentage,
            };

            if (discount) {
                updateDiscountMutation.mutate({
                    id: discount.id,
                    params,
                });
            } else {
                createScsDiscountMutation.mutate({
                    id: cxt.sc!.id,
                    params,
                });
            }
        })();
    };

    const handleOnDelete = () => {
        if (!discount) {
            return;
        }

        deleteDiscountMutation.mutate(discount.id);
    };

    const [isMutationsLoading, mutationsLoader] = useLoader([
        createScsDiscountMutation.status,
        updateDiscountMutation.status,
        deleteDiscountMutation.status,
    ]);

    if (isMutationsLoading) {
        return mutationsLoader;
    }

    return (
        <SttDialog
            title={discount ? discount.name : t('newDiscount')}
            open
            onClose={onClose}
            content={
                <>
                    <SttValidatedForm
                        form={form}
                        containerProps={{
                            spacing: 0,
                        }}
                        fields={[
                            {
                                type: 'textInput',
                                name: 'name',
                                caption: t('Name'),
                                rules: {
                                    required: true,
                                },
                            },
                            {
                                type: 'numberInput',
                                name: 'discountPercentage',
                                caption: t('Percentage'),
                                InputProps: {
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            %
                                        </InputAdornment>
                                    ),
                                },
                                rules: {
                                    required: true,
                                },
                                xs: 3,
                            },
                        ]}
                    />
                    <Box className="mt-4 flex items-start gap-2 rounded-lg bg-blue-50 p-3">
                        <InfoOutlinedIcon
                            className="mt-0.5 text-blue-600"
                            fontSize="small"
                        />
                        <Typography variant="body2" className="text-blue-800">
                            {t('discounts.infoText')}
                        </Typography>
                    </Box>
                </>
            }
            buttons={[
                {
                    show: !!discount && !discount.trash,
                    type: 'delete',
                    onClick: () => handleOnDelete(),
                    permission: PERMISSIONS.DISCOUNT.DELETE,
                },
                {
                    type: 'save',
                    onClick: () => handleOnSubmit(),
                    permission: discount
                        ? PERMISSIONS.DISCOUNT.MODIFY
                        : PERMISSIONS.DISCOUNT.CREATE,
                },
            ]}
        />
    );
}
